import React from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import './ShopResultsDisclaimer.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp, faChevronDown } from '@fortawesome/pro-regular-svg-icons';

import { getUnmatchedPins } from '../../../APIClient/pins';
import { isShopper, getUserId, getSettings } from '../../../Helpers/user_helpers';
import { getMatchingPriorityForUser } from '../../../Helpers/shop_helpers';
const ShopResultsDisclaimer = props => {
  /*
    For certain shop results, we need to display a disclaimer at the top of the results.
  */
  const { user, isYourShop } = props;
  const { isHighPriorityForMatching, isLowPriorityForMatching } = getSettings(user) || {};
  const needsToSeeDisclaimer = isYourShop && props.tab === 'latest' && !isShopper(user);

  const [isFetching, setIsFetching] = React.useState(true);
  const [unmatchedCountThisWeek, setUnmatchedCountThisWeek] = React.useState(null);
  const [recentUnmatchedPins, setRecentUnmatchedPins] = React.useState(null);
  const [currentlyIgnoredDomains, setCurrentlyIgnoredDomains] = React.useState([]);
  const [unmatchedDomainCounts, setUnmatchedDomainCounts] = React.useState({});

  React.useEffect(() => {
    if (needsToSeeDisclaimer) {
      setIsFetching(true);
      getUnmatchedPins({ User_id: getUserId(user) })
        .then(resp => {
          setUnmatchedCountThisWeek(resp.unmatched_count_this_week);
          setRecentUnmatchedPins(resp.recent_unmatched_pins);
          setCurrentlyIgnoredDomains(resp.currently_ignored_domains);
          setUnmatchedDomainCounts(resp.currently_unmatched_domain_counts);
        })
        .finally(() => {
          setIsFetching(false);
        });
    }
  }, [needsToSeeDisclaimer]);

  const toggleFAQ = () => {
    setIsFAQExpanded(!isFAQExpanded);
    window.__ADD_EVENT__('Shop - Latest Tab Processing FAQ Clicked');
  };

  const [isFAQExpanded, setIsFAQExpanded] = React.useState(false);
  const faqs = [
    {
      question: 'What does "processing" mean?',
      answer:
        "You're the first to link this product! Our team is creating a trusted product page with images, details, and where to buy — so your shop stays high quality."
    },
    {
      question: 'How long does it take?',
      answer: "Usually 24-48 hours. Our 50-person curation team works 7 days a week and we're always working to make it faster."
    },
    {
      question: 'Can I speed it up?',
      answer: 'Yes—higher creator tiers get prioritized in the queue.'
    },
    {
      question: 'Anything else I should know?',
      answer: _.filter([
        currentlyIgnoredDomains.length > 0
          ? `We're still learning how to process some sites, so links from the following may take a bit longer: ${currentlyIgnoredDomains
              .map(domain => `${domain} (${unmatchedDomainCounts[domain]})`)
              .join(', ')}. Thanks for your patience!`
          : null,
        isHighPriorityForMatching ? 'You have been marked a high priority for matching, so your products will be processed faster.' : null,
        isLowPriorityForMatching
          ? 'Due to your linking frequency we are currently only able to process a few links as high priority, we are working on increasing the pace but please know that it might take some time to fully ramp up with your pace of linking!'
          : null
      ]).join(' ')
    }
  ].filter(faq => !!faq.answer);

  if (!needsToSeeDisclaimer || isFetching || props.query) return null;

  // UI Handling for pin counts
  const hasMoreProcessing = unmatchedCountThisWeek > 2;

  // Priority Handling
  const priority = getMatchingPriorityForUser(user);

  return (
    <div className='shop-results-disclaimer-container'>
      <div className='shop-results-disclaimer'>
        <div onClick={toggleFAQ} className='processing-section'>
          <div className='main'>
            <div className='images'>
              {recentUnmatchedPins.slice(0, 2).map(pin => (
                <div className='image' key={pin.id}>
                  <img src={pin.image} alt={pin.title} />
                </div>
              ))}
              {hasMoreProcessing && (
                <div className='more-processing'>
                  <div className='count'>{unmatchedCountThisWeek - 2 > 100 ? '100+' : `+${unmatchedCountThisWeek - 2}`}</div>
                </div>
              )}
            </div>
            <div className='text'>
              <div className='title'>
                {unmatchedCountThisWeek > 1
                  ? `Product${unmatchedCountThisWeek === 1 ? '' : 's'} you added this week are being processed`
                  : 'A product you added this week is being processed'}
              </div>
              <div className='subtitle'>
                {priority === 0 || priority === 1
                  ? "We're building full product pages for them now — hang tight, they should appear in your shop within 24-48 hours."
                  : "We're building full product pages for them now — they'll appear in your shop once they're ready."}
              </div>
            </div>
          </div>
          <div className='learn-more'>
            Learn More <FontAwesomeIcon icon={isFAQExpanded ? faChevronUp : faChevronDown} />
          </div>
        </div>
        {isFAQExpanded && (
          <div className='faq-section'>
            {faqs.map(faq => (
              <div className='faq' key={faq.question}>
                <div className='question'>{faq.question}</div>
                <div className='answer'>{faq.answer}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

ShopResultsDisclaimer.propTypes = {
  user: PropTypes.object.isRequired,
  tab: PropTypes.string
};

export default ShopResultsDisclaimer;
